import { create } from 'zustand';

/**
 * Sidebar presentation preferences.
 *
 * Pinned  — the nav keeps its full width and pushes page content aside (the historical behaviour).
 * Unpinned — the nav collapses to the icon rail and only expands, as an overlay, while hovered.
 *
 * The preference is per-browser, so it lives in localStorage rather than on the user record.
 * `localStorage.clear()` on sign out wipes it, so the defaults below are what everyone gets on
 * their next sign-in.
 */
const SIDEBAR_PINNED_KEY = 'sidebarPinned';
const SIDEBAR_AUTO_HIDE_KEY = 'sidebarAutoHide';

/**
 * Defaults: unpinned, with hover reveal on. A fresh sign-in therefore starts on the icon rail and
 * gives the page its full width, and the nav still comes back on hover without needing a click.
 * Anyone who has already set a preference keeps it -- these only apply when the key is absent.
 */
const SIDEBAR_PINNED_DEFAULT = false;
const SIDEBAR_AUTO_HIDE_DEFAULT = true;

const readFlag = (key: string, fallback: boolean): boolean => {
  try {
    const stored = localStorage.getItem(key);
    return stored === null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
};

const writeFlag = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Preference storage is best-effort; a private-mode failure must not break the nav.
  }
};

/** Marks the rail's avatar, so the nav can tell "pointer on the avatar" from "pointer elsewhere on the nav". */
export const PROFILE_TRIGGER_ATTR = 'data-profile-trigger';

/** Which of the pane's two menus is showing while a patient is open. */
export type SidebarMenuTab = 'patient' | 'main';

export type SidebarStore = {
  isPinned: boolean;
  setIsPinned: (value: boolean) => void;
  togglePinned: () => void;
  /**
   * Whether hovering the rail reveals an unpinned menu.  Off, an unpinned menu opens only when
   * the toggle is clicked — for people who find a menu that moves under the pointer jumpy.
   */
  isAutoHideEnabled: boolean;
  toggleAutoHide: () => void;
  /**
   * Lifted out of the nav pane so the patient bar's name button can open the pane straight onto
   * "This Patient".  Session-only -- unlike the two flags above it is not a stored preference.
   */
  menuTab: SidebarMenuTab;
  setMenuTab: (value: SidebarMenuTab) => void;
  /**
   * The rail's profile popup, open while this holds its anchor.  Lifted here because the nav has
   * to know about it: hovering the avatar must not also expand the menu, and hovering anywhere
   * else on the nav closes the popup and hands the pointer back to the menu.  Session-only.
   */
  profileMenuAnchor: HTMLElement | null;
  setProfileMenuAnchor: (value: HTMLElement | null) => void;
};

export const useSidebarStore = create<SidebarStore>(set => ({
  isPinned: readFlag(SIDEBAR_PINNED_KEY, SIDEBAR_PINNED_DEFAULT),
  setIsPinned: value =>
    set(() => {
      writeFlag(SIDEBAR_PINNED_KEY, value);
      return { isPinned: value };
    }),
  togglePinned: () =>
    set(state => {
      writeFlag(SIDEBAR_PINNED_KEY, !state.isPinned);
      return { isPinned: !state.isPinned };
    }),
  isAutoHideEnabled: readFlag(SIDEBAR_AUTO_HIDE_KEY, SIDEBAR_AUTO_HIDE_DEFAULT),
  toggleAutoHide: () =>
    set(state => {
      writeFlag(SIDEBAR_AUTO_HIDE_KEY, !state.isAutoHideEnabled);
      return { isAutoHideEnabled: !state.isAutoHideEnabled };
    }),
  menuTab: 'patient',
  setMenuTab: value => set({ menuTab: value }),
  profileMenuAnchor: null,
  setProfileMenuAnchor: value => set({ profileMenuAnchor: value })
}));
